import { AppDialogService } from './../../services/app-dialog.service';
import { catchError, take, takeUntil } from 'rxjs/operators';
import { UntypedFormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
} from '@angular/core';
import { LocationService } from 'src/app/services/location.service';
import { OrganizationService } from 'src/app/services/organization.service';
import { TranslateService } from '@ngx-translate/core';
import { of, Subject } from 'rxjs';
import { AppAsyncFieldValidators, AppFieldValidators } from 'src/app/utilities/validators';

@Component({
  selector: 'app-new-location-form',
  templateUrl: './new-location-form.component.html',
  styleUrls: ['./new-location-form.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewLocationFormComponent implements AfterViewInit, OnDestroy {
  formGroup = this.formBuilder.group({});
  organizationList: any[] = [];
  primaryContactDropdown: any[] = [];
  locationNameDuplicateErrorText: string | null = null;
  private lastDuplicateLocationName: string | null = null;

  selectedOrgId: number | null = null;

  locationValidationModel = {
    organizationId: 0,
  };

  Validators = AppFieldValidators;
  AsyncValidators = AppAsyncFieldValidators;

  private destroy$ = new Subject<void>();
  private _timezoneList: any;

  get timezoneList() {
    return this._timezoneList;
  }
  set timezoneList(value: any) {
    this._timezoneList = value;
  }

  get organizationId() {
    return this.organizationIdFormControl?.value;
  }
  set organizationId(value: any) {
    this.organizationIdFormControl?.setValue(value);
  }
  get organizationIdFormControl() {
    return this.formGroup.get('organizationId');
  }

  get locationName() {
    return this.locationNameFormControl?.value;
  }
  set locationName(value: any) {
    this.locationNameFormControl?.setValue(value);
  }
  get locationNameFormControl() {
    return this.formGroup.get('locationName');
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<NewLocationFormComponent>,
    private formBuilder: UntypedFormBuilder,
    public locationService: LocationService,
    private organizationService: OrganizationService,
    private changeDetectorRef: ChangeDetectorRef,
    private AppDialogService: AppDialogService,
    private translateService: TranslateService
  ) {
    this.organizationList = data.organizationList;
    this.selectedOrgId = data.selectedOrgId;
    this.initializeDropDownData();
  }

  ngAfterViewInit(): void {
    const initialOrgId = this.selectedOrgId ?? this.organizationId;
    if (initialOrgId) {
      this.locationValidationModel.organizationId = initialOrgId;
      this.loadPrimaryContacts(initialOrgId);
    }

    this.formGroup.get('active')?.setValue(true);
    this.formGroup.get('isShipTo')?.setValue(false);

    //listen to org changes
    this.organizationIdFormControl?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const orgId = this.organizationId;
      this.locationValidationModel.organizationId = orgId;
      this.loadPrimaryContacts(orgId);
      this.clearLocationNameDuplicateState();
      this.locationNameFormControl?.updateValueAndValidity();
      this.changeDetectorRef.detectChanges();
    });

    this.locationNameFormControl?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        const locationName = value?.trim?.() ?? null;
        if (
          this.lastDuplicateLocationName &&
          locationName !== this.lastDuplicateLocationName
        ) {
          this.clearLocationNameDuplicateState();
          this.changeDetectorRef.markForCheck();
        }
      });

    this.locationNameFormControl?.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const locationName = this.locationNameFormControl?.value?.trim?.() ?? null;
        const hasDuplicateError = this.locationNameFormControl?.hasError('locationNameAlreadyExists') ?? false;

        if (hasDuplicateError) {
          this.lastDuplicateLocationName = locationName;
          this.locationNameDuplicateErrorText = this.translateService.instant('LocationForm.NameAlreadyExists');
        }

        this.changeDetectorRef.markForCheck();
      });

  }
  onCloseButtonClick() {
    this.closeSelf();
  }

  onCancelButtonClick() {
    this.closeSelf();
  }

  ngOnDestroy(): void {
    if (this.destroy$) {
      this.destroy$.next();
      this.destroy$.complete();
    }
  }

  onSaveButtonClick() {
    if (this.formGroup.dirty && this.formGroup.valid) {
      this.AppDialogService.showGeneralMessage(
        this.translateService.instant('LocationForm.Addingnewlocation') +
          ': ' +
          this.locationName +
          ' ...'
      );
      this.locationService.saveNewLocation(this.formGroup.value).subscribe(saveResults => {
        this.closeSelf(saveResults?.locationId);
      });
    }
  }

  onAddressChanged(address: any) {
    if (!address['address_line_1']) return;

    this.changeDetectorRef.markForCheck();
  }

  public formGroupErrors() {
      var keys = Object.keys(this.formGroup.controls)
      .filter(k => this.formGroup.get(k)?.invalid)
      .map(k => ({ key: k, errors: this.formGroup.get(k)?.errors }));
      return keys ?? [];
  }

  private clearLocationNameDuplicateState() {
    this.lastDuplicateLocationName = null;
    this.locationNameDuplicateErrorText = null;

    const currentErrors = this.locationNameFormControl?.errors;
    if (!currentErrors?.['locationNameAlreadyExists']) {
      return;
    }

    const { locationNameAlreadyExists, ...remainingErrors } = currentErrors;
    const nextErrors = Object.keys(remainingErrors).length > 0 ? remainingErrors : null;
    this.locationNameFormControl?.setErrors(nextErrors);
  }

  private loadPrimaryContacts(orgId: number) {
    if (!orgId) {
      this.primaryContactDropdown = [];
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.organizationService
      .getPrimaryContactList(orgId)
      .pipe(
        take(1),
        catchError(() => of([])),
      )
      .subscribe((response: any) => {
        this.primaryContactDropdown = response ?? [];
        const selectedPrimaryContactId = this.formGroup.get('primaryContactId')?.value;
        if (selectedPrimaryContactId && !this.primaryContactDropdown.some(contact => contact.value === selectedPrimaryContactId)) {
          this.formGroup.get('primaryContactId')?.setValue(null);
        }
        this.changeDetectorRef.markForCheck();
      });
  }

  private closeSelf(locationId?: number | null) {
    if (!locationId) {
      this.dialogRef.close();
      return;
    }

    this.dialogRef.close({
      locationName: this.locationName,
      locationId: locationId ?? 0,
      organizationId: this.organizationId,
    });
  }

  private initializeDropDownData() {
    this.locationService.getTimeZones().subscribe((response: any) => {
      this.timezoneList = response;
      this.changeDetectorRef.markForCheck();
    });
  }

  public displayLocationNameAlreadyExistsMessage() {
      const duplicateValue = this.locationNameFormControl?.value?.trim?.() ?? null;
      const duplicateMessage = this.translateService.instant('LocationForm.NameAlreadyExists');

      this.lastDuplicateLocationName = duplicateValue;
      this.locationNameDuplicateErrorText = duplicateMessage;

      const currentErrors = this.locationNameFormControl?.errors ?? {};
      this.locationNameFormControl?.setErrors({
        ...currentErrors,
        locationNameAlreadyExists: true,
      });
      this.locationNameFormControl?.markAsTouched();

      this.AppDialogService.showErrorMessage(duplicateMessage);
      this.changeDetectorRef.markForCheck();
  }
}
